import classNames from 'classnames';
import React, { FC, useEffect } from 'react';
import ReactDOM from 'react-dom';

export interface ContextMenuItem {
  key: string;
  label: string;
  shortcut?: string;
  disabled?: boolean;
  danger?: boolean;
  isDivider?: boolean;
  onClick?: () => void;
}

interface CanvasContextMenuProps {
  x: number;
  y: number;
  items: ContextMenuItem[];
  onClose: () => void;
}

const MENU_WIDTH = 208;
const ITEM_HEIGHT = 32;

export const CanvasContextMenu: FC<CanvasContextMenuProps> = ({ x, y, items, onClose }) => {
  useEffect(() => {
    const onAway = (e: MouseEvent | KeyboardEvent): void => {
      if (e instanceof KeyboardEvent && e.key !== 'Escape') return;
      onClose();
    };

    window.addEventListener('mousedown', onAway);
    window.addEventListener('keydown', onAway);
    return () => {
      window.removeEventListener('mousedown', onAway);
      window.removeEventListener('keydown', onAway);
    };
  }, [onClose]);

  const visibleItems = items.filter((item) => !item.isDivider || items.indexOf(item) !== 0);
  const estimatedHeight =
    visibleItems.filter((i) => !i.isDivider).length * ITEM_HEIGHT + visibleItems.filter((i) => i.isDivider).length * 9;

  const clampedX = Math.min(x, window.innerWidth - MENU_WIDTH - 4);
  const clampedY = Math.min(y, window.innerHeight - estimatedHeight - 4);

  return ReactDOM.createPortal(
    <ul
      className="fixed bg-white border border-secondaryAccent rounded-md shadow-lg py-1 text-sm z-[9999]"
      style={{ left: clampedX, top: clampedY, width: MENU_WIDTH }}
      onMouseDown={(e) => {
        e.stopPropagation();
      }}
    >
      {items.map((item) => {
        if (item.isDivider) {
          return <li key={item.key} className="border-t border-secondaryAccent my-1" />;
        }

        return (
          <li
            key={item.key}
            className={classNames('px-3 py-1.5 flex justify-between items-center cursor-pointer select-none', {
              'hover:bg-secondaryAccent': !item.disabled,
              'opacity-40 pointer-events-none': item.disabled,
              'text-error': item.danger,
            })}
            onClick={() => {
              item.onClick?.();
              onClose();
            }}
          >
            <span>{item.label}</span>
            {item.shortcut && <span className="text-primaryLight text-xs">{item.shortcut}</span>}
          </li>
        );
      })}
    </ul>,
    document.body,
  );
};
